import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiBase } from '../api/api';
import InventoryAgingCriteriaModal from '../components/inventory/InventoryAgingCriteriaModal';
import BpGoldenArrow from '../components/financials/BpGoldenArrow';
import { getToken } from '../utils/session';
import {
  buildInventoryAgingQuery,
  defaultInventoryAgingCriteria,
  inventoryAgingGridBands,
  parseIntervalDaysStrict,
  sumBucketKeys,
} from '../utils/inventoryAgingQuery';

/** SAP EU-style numbers (e.g. 7.000,000 qty; 945,00 EUR) */
const fmtQty = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const fmtVal = (v, cur) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${n} ${cur}` : n;
};

/** SAP-style: leave bucket cells blank when amount is zero (no 0,000 / 0,00). */
function isZeroish(n) {
  const x = Number(n);
  return !Number.isFinite(x) || Math.abs(x) < 1e-6;
}

const C = {
  th: 'border border-sap-border/80 px-4 py-3 font-semibold text-2xs uppercase tracking-wider text-sap-textMuted bg-gray-50',
  thNum: 'border border-sap-border/80 px-4 py-3 font-semibold text-2xs uppercase tracking-wider text-sap-textMuted bg-gray-50 text-right',
  det: 'border border-sap-border/80 px-2 py-1.5 text-sap-text',
  detAlt: 'border border-sap-border/80 px-2 py-1.5 bg-sap-bg/40 text-sap-text',
  detNum: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums text-sap-text',
  detNumAlt: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums bg-sap-bg/40 text-sap-text',
  whTot: 'border border-sap-border/80 px-2 py-1.5 font-bold text-blue-700 bg-blue-50/60',
  whTotNum: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums font-bold text-blue-700 bg-blue-50/60',
  itTot: 'border border-sap-border/80 px-2 py-1.5 font-bold text-red-700 bg-red-50/50',
  itTotNum: 'border border-sap-border/80 px-2 py-1.5 text-right tabular-nums font-bold text-red-700 bg-red-50/50',
};

export default function InventoryAgingReport() {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState(() => defaultInventoryAgingCriteria());
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [applied, setApplied] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [paginationMeta, setPaginationMeta] = useState({
    total_items: 0,
    total_pages: 1,
    page: 1,
    items_per_page: 15,
  });
  /** Intervals used for column headers — synced from API response after load. */
  const [reportIntervals, setReportIntervals] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const [exportPreviewError, setExportPreviewError] = useState('');
  const [exportPreviewRows, setExportPreviewRows] = useState([]);
  const [exportPreviewBands, setExportPreviewBands] = useState([]);
  const [exportDownloadKind, setExportDownloadKind] = useState(null);
  const [exportDownloadMsg, setExportDownloadMsg] = useState('');

  const load = useCallback(async () => {
    if (!applied) {
      setRows([]);
      setReportIntervals(null);
      setPaginationMeta({ total_items: 0, total_pages: 1, page: 1, items_per_page: itemsPerPage });
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const qs = buildInventoryAgingQuery(criteria, search, { page, itemsPerPage });
      const res = await api.get(`/inventory/aging/report?${qs}`);
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      if (Array.isArray(res?.intervals) && res.intervals.length) {
        setReportIntervals(
          res.intervals.map((x) => parseInt(String(x), 10)).filter((n) => Number.isFinite(n) && n > 0)
        );
      } else {
        setReportIntervals(parseIntervalDaysStrict(criteria.intervalDays));
      }
      const pg = res?.pagination;
      if (pg && typeof pg === 'object') {
        setPaginationMeta({
          total_items: Number(pg.total_items) || 0,
          total_pages: Math.max(1, Number(pg.total_pages) || 1),
          page: Number(pg.page) || 1,
          items_per_page: Number(pg.items_per_page) || itemsPerPage,
        });
        if (Number(pg.page) && Number(pg.page) !== page) {
          setPage(Number(pg.page));
        }
      }
    } catch (e) {
      setRows([]);
      setLoadError(e?.message || 'Failed to load inventory aging.');
    } finally {
      setLoading(false);
    }
  }, [applied, criteria, search, page, itemsPerPage]);

  useEffect(() => {
    if (!exportOpen || !applied) return undefined;
    let cancelled = false;
    (async () => {
      setExportPreviewLoading(true);
      setExportPreviewError('');
      try {
        const qs = buildInventoryAgingQuery(criteria, search, { page: 1, itemsPerPage: 100 });
        const res = await api.get(`/inventory/aging/report?${qs}`);
        if (cancelled) return;
        setExportPreviewRows(Array.isArray(res?.rows) ? res.rows : []);
        const ints =
          Array.isArray(res?.intervals) && res.intervals.length
            ? res.intervals.map((x) => parseInt(String(x), 10)).filter((n) => Number.isFinite(n) && n > 0)
            : parseIntervalDaysStrict(criteria.intervalDays);
        const { bands: pb } = inventoryAgingGridBands(ints.length ? ints : [30, 60, 90, 120]);
        setExportPreviewBands(pb);
      } catch (e) {
        if (!cancelled) {
          setExportPreviewRows([]);
          setExportPreviewBands([]);
          setExportPreviewError(e?.message || 'Could not load preview.');
        }
      } finally {
        if (!cancelled) setExportPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportOpen, applied, criteria, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openItemMaster = useCallback(
    (itemNo) => {
      if (!itemNo) return;
      navigate(`/item-master/${encodeURIComponent(itemNo)}`);
    },
    [navigate]
  );

  const parsedIntervalDays = useMemo(
    () => parseIntervalDaysStrict(criteria.intervalDays),
    [criteria.intervalDays]
  );

  const intervalsForGrid = reportIntervals ?? parsedIntervalDays;
  const { bands } = inventoryAgingGridBands(intervalsForGrid);
  const metricGroupCount = 1 + bands.length;
  const flatColCount = 8 + 2 * metricGroupCount;

  const renderMetricCells = (buckets, currency, styleNum) => {
    const total = buckets?.total || { qty: 0, value: 0 };
    return (
      <>
        <td className={styleNum}>{fmtQty(total.qty)}</td>
        <td className={styleNum}>{fmtVal(total.value, currency)}</td>
        {bands.map((band) => {
          const s = sumBucketKeys(buckets, band.bucketKeys);
          const blankQty = isZeroish(s.qty);
          const blankVal = isZeroish(s.value);
          return (
            <React.Fragment key={band.groupLabel}>
              <td className={styleNum}>{blankQty ? '' : fmtQty(s.qty)}</td>
              <td className={styleNum}>{blankVal ? '' : fmtVal(s.value, currency)}</td>
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const previewBands = exportPreviewBands.length ? exportPreviewBands : bands;

  const renderPreviewMetricCells = (buckets, currency, styleNum) => {
    const total = buckets?.total || { qty: 0, value: 0 };
    return (
      <>
        <td className={styleNum}>{fmtQty(total.qty)}</td>
        <td className={styleNum}>{fmtVal(total.value, currency)}</td>
        {previewBands.map((band) => {
          const s = sumBucketKeys(buckets, band.bucketKeys);
          const blankQty = isZeroish(s.qty);
          const blankVal = isZeroish(s.value);
          return (
            <React.Fragment key={`pv-${band.groupLabel}`}>
              <td className={styleNum}>{blankQty ? '' : fmtQty(s.qty)}</td>
              <td className={styleNum}>{blankVal ? '' : fmtVal(s.value, currency)}</td>
            </React.Fragment>
          );
        })}
      </>
    );
  };

  let lineIdx = 0;

  const ipp = paginationMeta.items_per_page || itemsPerPage;
  const curPg = paginationMeta.page || page;
  const totalPg = paginationMeta.total_pages || 1;
  const totalIt = paginationMeta.total_items || 0;
  const rangeFrom = totalIt === 0 ? 0 : (curPg - 1) * ipp + 1;
  const rangeTo = totalIt === 0 ? 0 : Math.min(curPg * ipp, totalIt);

  const exportQueryString = useMemo(
    () => buildInventoryAgingQuery(criteria, search, null),
    [criteria, search]
  );

  const downloadInventoryExport = useCallback(
    async (kind) => {
      if (!applied) return;
      setExportDownloadKind(kind);
      setExportDownloadMsg('');
      const token = getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const ts = Date.now();
      const safeDate = String(criteria.reportDate || 'report').replace(/[^\w.-]/g, '_');
      try {
        let url;
        let filename;
        if (kind === 'pdf') {
          const qs = `${exportQueryString}&download=1&_ts=${ts}`;
          url = `${getApiBase()}/inventory/aging/report/pdf?${qs}`;
          filename = `inventory_aging_${safeDate}.pdf`;
        } else {
          const qs = `${exportQueryString}&_ts=${ts}`;
          url = `${getApiBase()}/inventory/aging/report/excel?${qs}`;
          filename = `inventory_aging_${safeDate}.csv`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) {
          throw new Error(`${kind === 'pdf' ? 'PDF' : 'Excel'} failed (${res.status})`);
        }
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      } catch (e) {
        setExportDownloadMsg(e?.message || 'Download failed');
      } finally {
        setExportDownloadKind(null);
      }
    },
    [applied, criteria.reportDate, exportQueryString]
  );

  return (
    <div className="space-y-4 font-sap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            title="Back to selection criteria"
            onClick={() => setCriteriaOpen(true)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sap-blue text-white hover:bg-sap-blueDark shadow-sm shrink-0"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h1 className="page-title">Inventory Aging Report</h1>
            {applied && (
              <p className="text-2xs text-sap-textLight mt-0.5 tabular-nums">
                Age bands (day limits):{' '}
                {parsedIntervalDays.length ? parsedIntervalDays.join(', ') : '—'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setCriteriaOpen(true)}
            className="btn-primary"
          >
            Inventory Aging Report Filter
          </button>
          <button
            type="button"
            disabled={!applied || loading || totalIt === 0}
            onClick={() => {
              setExportPreviewRows([]);
              setExportPreviewBands([]);
              setExportPreviewError('');
              setExportDownloadMsg('');
              setExportDownloadKind(null);
              setExportOpen(true);
            }}
            title="Preview data and download PDF or Excel"
            className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-40 disabled:pointer-events-none"
          >
            Export…
          </button>
        </div>
      </div>

      {loadError && (
        <div className="px-3 py-2 rounded-lg text-xs bg-amber-50 border border-sap-warning/40 text-sap-text">
          {loadError}
        </div>
      )}

      <div className="flex gap-3 items-center flex-wrap">
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          disabled={!applied}
          placeholder="Search item no., description, project…"
          className="search-input flex-1 min-w-[160px] disabled:opacity-50"
        />
        <label className="flex items-center gap-1.5 text-xs text-sap-text shrink-0">
          <span className="text-sap-textLight whitespace-nowrap">Items / page</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(1);
            }}
            disabled={!applied}
            className="border border-sap-border rounded-xl px-4 py-2.5 text-sm bg-white text-sap-text focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue disabled:opacity-50"
          >
            {[10, 15, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {applied && !loading && totalIt > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-sap-text">
          <span className="text-sap-textLight">
            Items <span className="text-sap-text font-medium">{rangeFrom}</span>–
            <span className="text-sap-text font-medium">{rangeTo}</span> of{' '}
            <span className="text-sap-text font-medium">{totalIt}</span>
            <span className="hidden sm:inline"> (page {curPg} of {totalPg})</span>
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={curPg <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={curPg >= totalPg || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <div className="data-table-wrap">
        <div className="max-h-[min(70vh,720px)] overflow-auto">
          <table
            className="w-full text-xs border-collapse min-w-[1200px]"
            style={{ minWidth: Math.max(1200, 640 + metricGroupCount * 130) }}
          >
          <thead>
            <tr>
              <th colSpan={8} className={`${C.th} text-left`} aria-label="Item and warehouse" />
              <th colSpan={2} className={`${C.th} text-center whitespace-nowrap`}>
                Total
              </th>
              {bands.map((band) => (
                <th
                  key={band.groupLabel}
                  colSpan={2}
                  className={`${C.th} text-center whitespace-nowrap normal-case`}
                >
                  {band.groupLabel}
                </th>
              ))}
            </tr>
            <tr>
              <th className={`${C.th} text-left`}>Item No.</th>
              <th className={`${C.th} text-left`}>Item Description</th>
              <th className={`${C.th} text-center`}>Whse</th>
              <th className={`${C.th} text-center`}>Serial No.</th>
              <th className={`${C.th} text-center`}>Batch No.</th>
              <th className={`${C.th} text-center`}>Base Unit</th>
              <th className={`${C.th} text-left`}>Project Code</th>
              <th className={`${C.th} text-left`}>Project Name</th>
              {Array.from({ length: metricGroupCount }).map((_, gi) => (
                <React.Fragment key={`mhdr-${gi}`}>
                  <th className={C.thNum}>Quantity</th>
                  <th className={C.thNum}>Value</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={flatColCount} className="border border-sap-border px-3 py-8 text-center text-sap-textLight">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={flatColCount} className="border border-sap-border px-3 py-8 text-center text-sap-textLight">
                  {applied
                    ? 'No stock matches the current criteria.'
                    : 'Open the filter, set your criteria, then click OK to run the report.'}
                </td>
              </tr>
            ) : (
              rows.map((r, ri) => {
                if (r.kind === 'line') {
                  lineIdx += 1;
                  const alt = lineIdx % 2 === 0;
                  return (
                    <tr key={`row-${ri}`} className={alt ? 'bg-sap-bg/30' : ''}>
                      <td className={alt ? C.det : C.detAlt}>
                        <div className="flex items-center gap-1 min-w-0">
                          <BpGoldenArrow title="Item Master Data" onClick={() => openItemMaster(r.item_no)} />
                          <span className="font-medium truncate tabular-nums">{r.item_no}</span>
                        </div>
                      </td>
                      <td className={alt ? C.det : C.detAlt}>
                        <span className="truncate block max-w-[220px]" title={r.description}>
                          {r.description}
                        </span>
                      </td>
                      <td className={`${alt ? C.det : C.detAlt} text-center tabular-nums`}>{r.wh_code}</td>
                      <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.serial_no || '—'}</td>
                      <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.batch_no || '—'}</td>
                      <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.base_uom || '—'}</td>
                      <td className={`${alt ? C.det : C.detAlt} tabular-nums`} title={r.project_code || ''}>
                        {r.project_code || '—'}
                      </td>
                      <td className={alt ? C.det : C.detAlt}>
                        <span className="truncate block max-w-[180px]" title={r.project_name || ''}>
                          {r.project_name || '—'}
                        </span>
                      </td>
                      {renderMetricCells(r.buckets, r.currency, alt ? C.detNum : C.detNumAlt)}
                    </tr>
                  );
                }
                if (r.kind === 'wh_total') {
                  const tq = r.wh_total_qty != null ? Number(r.wh_total_qty) : Number(r.buckets?.total?.qty);
                  const rq = r.wh_remaining_qty != null ? Number(r.wh_remaining_qty) : 0;
                  const whLbl = String(r.wh_code || '').trim() || '—';
                  const sumText = `Item ${r.item_no || '—'} in Warehouse ${whLbl} Total = ${fmtQty(tq)}; Remaining = ${fmtQty(rq)}`;
                  return (
                    <tr key={`row-${ri}`}>
                      <td className={C.whTot}>
                        <div className="flex items-center gap-1 min-w-0">
                          <BpGoldenArrow title="Item Master Data" onClick={() => openItemMaster(r.item_no)} />
                          <span className="tabular-nums">{r.item_no || '—'}</span>
                        </div>
                      </td>
                      <td className={`${C.whTot} text-left`} title="Remaining = quantity on postings without a project code (unassigned stock in this warehouse).">
                        {sumText}
                      </td>
                      <td className={`${C.whTot} text-center`}>—</td>
                      <td className={`${C.whTot} text-center`}>—</td>
                      <td className={`${C.whTot} text-center`}>—</td>
                      <td className={`${C.whTot} text-center`}>—</td>
                      <td className={`${C.whTot} tabular-nums`}>—</td>
                      <td className={C.whTot}>—</td>
                      {renderMetricCells(r.buckets, r.currency, C.whTotNum)}
                    </tr>
                  );
                }
                if (r.kind === 'item_total') {
                  return (
                    <React.Fragment key={`frag-${ri}`}>
                      <tr>
                        <td className={`${C.itTot} text-left`}>Item {r.item_no} Total</td>
                        <td className={C.itTot}>—</td>
                        <td className={`${C.itTot} text-center`}>—</td>
                        <td className={`${C.itTot} text-center`}>—</td>
                        <td className={`${C.itTot} text-center`}>—</td>
                        <td className={`${C.itTot} text-center`}>—</td>
                        <td className={`${C.itTot} tabular-nums`}>{r.project_code || '—'}</td>
                        <td className={C.itTot}>
                          <span className="truncate block max-w-[180px]" title={r.project_name || ''}>
                            {r.project_name || '—'}
                          </span>
                        </td>
                        {renderMetricCells(r.buckets, r.currency, C.itTotNum)}
                      </tr>
                      <tr aria-hidden="true" className="pointer-events-none">
                        <td colSpan={flatColCount} className="h-3 p-0 border-0 bg-[#ECEFF1]/90" />
                      </tr>
                    </React.Fragment>
                  );
                }
                return null;
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {exportOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 bg-black/45"
          role="dialog"
          aria-modal="true"
          aria-labelledby="inv-aging-export-title"
        >
          <div className="bg-white rounded-xl shadow-xl border border-sap-border w-full max-w-[min(1100px,96vw)] max-h-[min(88vh,820px)] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-sap-border flex flex-wrap items-start justify-between gap-2 bg-[#F7FAF9] shrink-0">
              <div>
                <h2 id="inv-aging-export-title" className="page-title">
                  Export inventory aging
                </h2>
                <p className="text-2xs text-sap-textLight mt-1 max-w-xl">
                  Data preview (first 100 rows on page 1 for this filter). Then download ProERP-style PDF or Excel for
                  all matching items ({totalIt.toLocaleString()} total, export cap 12,000).
                </p>
              </div>
              <button
                type="button"
                className="text-sap-textLight hover:text-sap-text text-xl leading-none px-2 py-0.5 rounded hover:bg-black/5"
                onClick={() => setExportOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-auto px-2 py-2">
              {exportPreviewError ? (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                  {exportPreviewError}
                </div>
              ) : null}
              {exportPreviewLoading ? (
                <div className="text-xs text-sap-textLight py-12 text-center">Loading preview…</div>
              ) : (
                <div className="overflow-x-auto border border-sap-border/60 rounded-lg">
                  <table className="w-full text-2xs border-collapse min-w-[760px]">
                    <thead>
                      <tr>
                        <th colSpan={8} className={`${C.th} text-left`} aria-hidden />
                        <th colSpan={2} className={`${C.th} text-center whitespace-nowrap`}>
                          Total
                        </th>
                        {previewBands.map((band) => (
                          <th
                            key={`ph-${band.groupLabel}`}
                            colSpan={2}
                            className={`${C.th} text-center whitespace-nowrap normal-case`}
                          >
                            {band.groupLabel}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <th className={`${C.th} text-left`}>Item No.</th>
                        <th className={`${C.th} text-left`}>Description</th>
                        <th className={`${C.th} text-center`}>Whse</th>
                        <th className={`${C.th} text-center`}>Serial</th>
                        <th className={`${C.th} text-center`}>Batch</th>
                        <th className={`${C.th} text-center`}>UOM</th>
                        <th className={`${C.th} text-left`}>Project</th>
                        <th className={`${C.th} text-left`}>Project name</th>
                        {Array.from({ length: 1 + previewBands.length }).map((_, gi) => (
                          <React.Fragment key={`pvh-${gi}`}>
                            <th className={C.thNum}>Qty</th>
                            <th className={C.thNum}>Value</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let pv = 0;
                        return exportPreviewRows.map((r, ri) => {
                          if (r.kind === 'line') {
                            pv += 1;
                            const alt = pv % 2 === 0;
                            return (
                              <tr key={`ep-${ri}`} className={alt ? 'bg-sap-bg/30' : ''}>
                                <td className={alt ? C.det : C.detAlt}>
                                  <span className="font-medium tabular-nums">{r.item_no}</span>
                                </td>
                                <td className={alt ? C.det : C.detAlt}>
                                  <span className="truncate block max-w-[140px]" title={r.description}>
                                    {r.description}
                                  </span>
                                </td>
                                <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.wh_code}</td>
                                <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.serial_no || '—'}</td>
                                <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.batch_no || '—'}</td>
                                <td className={`${alt ? C.det : C.detAlt} text-center`}>{r.base_uom || '—'}</td>
                                <td className={alt ? C.det : C.detAlt}>{r.project_code || '—'}</td>
                                <td className={alt ? C.det : C.detAlt}>
                                  <span className="truncate block max-w-[120px]" title={r.project_name || ''}>
                                    {r.project_name || '—'}
                                  </span>
                                </td>
                                {renderPreviewMetricCells(r.buckets, r.currency, alt ? C.detNum : C.detNumAlt)}
                              </tr>
                            );
                          }
                          if (r.kind === 'wh_total') {
                            const tq = r.wh_total_qty != null ? Number(r.wh_total_qty) : Number(r.buckets?.total?.qty);
                            const rq = r.wh_remaining_qty != null ? Number(r.wh_remaining_qty) : 0;
                            const whLbl = String(r.wh_code || '').trim() || '—';
                            const sumText = `Item ${r.item_no || '—'} in Warehouse ${whLbl} Total = ${fmtQty(tq)}; Remaining = ${fmtQty(rq)}`;
                            return (
                              <tr key={`ep-${ri}`}>
                                <td className={C.whTot}>
                                  <span className="tabular-nums">{r.item_no || '—'}</span>
                                </td>
                                <td className={`${C.whTot} text-left max-w-[200px]`} title={sumText}>
                                  <span className="line-clamp-2">{sumText}</span>
                                </td>
                                <td className={`${C.whTot} text-center`}>—</td>
                                <td className={`${C.whTot} text-center`}>—</td>
                                <td className={`${C.whTot} text-center`}>—</td>
                                <td className={`${C.whTot} text-center`}>—</td>
                                <td className={C.whTot}>—</td>
                                <td className={C.whTot}>—</td>
                                {renderPreviewMetricCells(r.buckets, r.currency, C.whTotNum)}
                              </tr>
                            );
                          }
                          if (r.kind === 'item_total') {
                            return (
                              <tr key={`ep-${ri}`}>
                                <td className={`${C.itTot} text-left`}>Item {r.item_no} Total</td>
                                <td className={C.itTot}>—</td>
                                <td className={`${C.itTot} text-center`}>—</td>
                                <td className={`${C.itTot} text-center`}>—</td>
                                <td className={`${C.itTot} text-center`}>—</td>
                                <td className={`${C.itTot} text-center`}>—</td>
                                <td className={`${C.itTot} tabular-nums`}>—</td>
                                <td className={C.itTot}>—</td>
                                {renderPreviewMetricCells(r.buckets, r.currency, C.itTotNum)}
                              </tr>
                            );
                          }
                          return null;
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="w-full text-xs text-red-600 min-h-[1.25rem]">{exportDownloadMsg || ''}</div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setExportOpen(false)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-sap-border text-sap-text hover:bg-sap-bg/40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!applied || exportPreviewLoading || Boolean(exportPreviewError) || exportDownloadKind !== null}
                onClick={() => downloadInventoryExport('excel')}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:pointer-events-none"
              >
                {exportDownloadKind === 'excel' ? 'Preparing Excel…' : 'Download Excel'}
              </button>
              <button
                type="button"
                disabled={!applied || exportPreviewLoading || Boolean(exportPreviewError) || exportDownloadKind !== null}
                onClick={() => downloadInventoryExport('pdf')}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sap-blue text-white hover:bg-sap-blueDark disabled:opacity-40 disabled:pointer-events-none"
              >
                {exportDownloadKind === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      <InventoryAgingCriteriaModal
        open={criteriaOpen}
        value={criteria}
        onClose={() => setCriteriaOpen(false)}
        onApply={(next) => {
          setCriteria(next);
          setReportIntervals(parseIntervalDaysStrict(next.intervalDays));
          setApplied(true);
          setPage(1);
        }}
      />
    </div>
  );
}
